import { createEffect, sample } from 'effector';
import { keyBy } from 'lodash';
import $$api from 'models/api';
import { $notifiable } from 'models/participants';
import { $global } from 'models/store-global';

import AlertsService from 'services/AlertsService';

import { MyStore } from 'types/Store';

sample({
    clock: [
        $$api.qInstances.getChangesFx.failData,
        $$api.qInstances.createIncludedInstanceFx.failData,
        $$api.qInstances.createInstanceFx.failData,
        $$api.qInstances.deleteInstanceFx.failData,
        $$api.qInstances.getDocumentsForQInstanceFx.failData,
        $$api.qInstances.getDocumentTypesForQInstanceFx.failData,
        $$api.qInstances.getInstancesFx.failData,
        $$api.qInstances.getJobStatusFx.failData,
        $$api.qInstances.getNotifiableParticipantsFx.failData,
        $$api.qInstances.getQInstanceFx.failData,
        $$api.qInstances.getSummaryFx.failData,
        $$api.qInstances.isPaymentOkFx.failData,
        $$api.qInstances.loadChildrenFx.failData,
        $$api.qInstances.rollbackFx.failData,
        $$api.qInstances.setLocationFx.failData,
        $$api.qInstances.setStatusFx.failData,
        $$api.qInstances.setToBeMailedFx.failData,
        $$api.qInstances.shipDocsFx.failData,
        $$api.qInstances.startPaymentFx.failData,
        $$api.qInstances.updateNotifiableSettingsFx.failData,
        $$api.qInstances.sendInvoiceFx.failData,
    ],
    target: createEffect(AlertsService.handleError),
});

// Handle createInstance success
sample({
    source: $global,
    clock: $$api.qInstances.createInstanceFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        qInstances: {
            ...(store.qInstances || {}),
            [data.questionnaire_instance_id]: data,
        },
    }),
    target: $global,
});

// Handle deleteInstance success
sample({
    source: $global,
    clock: $$api.qInstances.deleteInstanceFx.done.map(({ params: { id } }) => id),
    fn: (global, id) => {
        delete global.qInstances?.[id];
        return global;
    },
    target: $global,
});

// Handle getDocumentsForQInstance success
sample({
    source: $global,
    clock: $$api.qInstances.getDocumentsForQInstanceFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        temp: {
            ...(store.temp || {}),
            documents_in_progress: {
                ...(store.temp?.documents_in_progress || {}),
                documents: data,
            },
        },
    }),
    target: $global,
});

// Handle getDocumentTypesForQInstance success
sample({
    source: $global,
    clock: $$api.qInstances.getDocumentTypesForQInstanceFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        temp: {
            ...(store.temp || {}),
            documents_in_progress: {
                ...(store.temp?.documents_in_progress || {}),
                documentTypes: data.document_types,
            },
        },
    }),
    target: $global,
});

// Handle getInstances success
sample({
    source: $global,
    clock: $$api.qInstances.getInstancesFx.done.map(({ result: qInstances, params: participantId }) => ({
        qInstances,
        participantId,
    })),
    fn: (store, { qInstances, participantId }): MyStore => {
        const result = keyBy(qInstances, 'questionnaire_instance_id');
        if (participantId) {
            return {
                ...store,
                advisorClient: {
                    ...(store.advisorClient || {}),
                    clients: {
                        ...(store.advisorClient?.clients || {}),
                        [participantId]: {
                            ...(store.advisorClient?.clients?.[participantId] || {}),
                            qInstances: result,
                        },
                    },
                },
            };
        }

        return {
            ...store,
            qInstances: result,
        };
    },
    target: $global,
});

// Handle getNotifiableParticipants success
sample({
    clock: $$api.qInstances.getNotifiableParticipantsFx.doneData,
    target: $notifiable,
});

// Handle getQInstance success
sample({
    source: $global,
    clock: $$api.qInstances.getQInstanceFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        qInstances: {
            ...(store.qInstances || {}),
            [data.questionnaire_instance_id]: data,
        },
    }),
    target: $global,
});

// Handle getSummary success
sample({
    source: $global,
    clock: $$api.qInstances.getSummaryFx.done.map(({ params: { id, section }, result: data }) => ({
        id,
        section,
        data,
    })),
    fn: (store, { id, section, data }): MyStore => {
        const isPayed = store.isPayed;
        data.q_instance_id = id;
        data.parent_host = window.location.origin;
        data.section = section;
        data.is_payed = isPayed?.[id];
        return {
            ...store,
            qInstanceSummary: {
                ...(store.qInstanceSummary || {}),
                [id]: data,
            },
        };
    },
    target: $global,
});

// Handle isPaymentOk success
sample({
    source: $global,
    clock: $$api.qInstances.isPaymentOkFx.done.map(({ params: questionnaire_instance_id, result: data }) => ({
        questionnaire_instance_id,
        data,
    })),
    fn: (store, { questionnaire_instance_id, data }): MyStore => ({
        ...store,
        isPayed: {
            ...(store.isPayed || {}),
            [questionnaire_instance_id]: data.result,
        },
    }),
    target: $global,
});

// Handle setLocation success
sample({
    source: $global,
    clock: $$api.qInstances.setLocationFx.done.map(({ params: { id, location } }) => ({ id, location })),
    fn: (store, { id, location }): MyStore => {
        if (!store.qInstances?.[id]) {
            return store;
        }
        return {
            ...store,
            qInstances: {
                ...store.qInstances,
                [id]: {
                    ...store.qInstances[id],
                    location_description: location,
                },
            },
        };
    },
    target: $global,
});

// Handle setStatus success
sample({
    source: $global,
    clock: $$api.qInstances.setStatusFx.done.map(({ params: { id, status } }) => ({ id, status })),
    fn: (store, { id, status }): MyStore => {
        if (!store.qInstances?.[id]) {
            return store;
        }
        return {
            ...store,
            qInstances: {
                ...store.qInstances,
                [id]: {
                    ...store.qInstances[id],
                    status: status,
                },
            },
        };
    },
    target: $global,
});
